import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, FileUp, ShieldCheck, X } from "lucide-react";

const STEP_TITLES = ["Privacy", "Preferenze corso", "Dati anagrafici", "Documenti"];

const ANAG_FIELDS = [
  { key: "nome", label: "Nome", testid: "form-field-nome" },
  { key: "cognome", label: "Cognome", testid: "form-field-cognome" },
  { key: "data_nascita", label: "Data di nascita", type: "date", testid: "form-field-data-nascita" },
  { key: "luogo_nascita", label: "Luogo di nascita", testid: "form-field-luogo-nascita" },
  { key: "codice_fiscale", label: "Codice fiscale", testid: "form-field-cf" },
  { key: "nazionalita", label: "Nazionalità", testid: "form-field-nazionalita" },
  { key: "indirizzo", label: "Indirizzo", testid: "form-field-indirizzo" },
  { key: "numero_civico", label: "Numero civico", testid: "form-field-numero-civico" },
  { key: "comune", label: "Città", testid: "form-field-comune" },
  { key: "provincia", label: "Provincia", testid: "form-field-provincia", select: true },
  { key: "cap", label: "Codice postale (CAP)", testid: "form-field-cap" },
  { key: "telefono", label: "Telefono", testid: "form-field-telefono" },
];

const FIELD_LABELS = Object.fromEntries(ANAG_FIELDS.map((f) => [f.key, f.label]));
FIELD_LABELS.permesso_soggiorno = "Permesso di soggiorno";

const PROVINCE = [
  ["AG", "Agrigento"], ["AL", "Alessandria"], ["AN", "Ancona"], ["AO", "Aosta"], ["AR", "Arezzo"],
  ["AP", "Ascoli Piceno"], ["AT", "Asti"], ["AV", "Avellino"], ["BA", "Bari"], ["BT", "Barletta-Andria-Trani"],
  ["BL", "Belluno"], ["BN", "Benevento"], ["BG", "Bergamo"], ["BI", "Biella"], ["BO", "Bologna"],
  ["BZ", "Bolzano"], ["BS", "Brescia"], ["BR", "Brindisi"], ["CA", "Cagliari"], ["CL", "Caltanissetta"],
  ["CB", "Campobasso"], ["CE", "Caserta"], ["CT", "Catania"], ["CZ", "Catanzaro"], ["CH", "Chieti"],
  ["CO", "Como"], ["CS", "Cosenza"], ["CR", "Cremona"], ["KR", "Crotone"], ["CN", "Cuneo"],
  ["EN", "Enna"], ["FM", "Fermo"], ["FE", "Ferrara"], ["FI", "Firenze"], ["FG", "Foggia"],
  ["FC", "Forlì-Cesena"], ["FR", "Frosinone"], ["GE", "Genova"], ["GO", "Gorizia"], ["GR", "Grosseto"],
  ["IM", "Imperia"], ["IS", "Isernia"], ["AQ", "L'Aquila"], ["SP", "La Spezia"], ["LT", "Latina"],
  ["LE", "Lecce"], ["LC", "Lecco"], ["LI", "Livorno"], ["LO", "Lodi"], ["LU", "Lucca"],
  ["MC", "Macerata"], ["MN", "Mantova"], ["MS", "Massa-Carrara"], ["MT", "Matera"], ["ME", "Messina"],
  ["MI", "Milano"], ["MO", "Modena"], ["MB", "Monza e Brianza"], ["NA", "Napoli"], ["NO", "Novara"],
  ["NU", "Nuoro"], ["OR", "Oristano"], ["PD", "Padova"], ["PA", "Palermo"], ["PR", "Parma"],
  ["PV", "Pavia"], ["PG", "Perugia"], ["PU", "Pesaro e Urbino"], ["PE", "Pescara"], ["PC", "Piacenza"],
  ["PI", "Pisa"], ["PT", "Pistoia"], ["PN", "Pordenone"], ["PZ", "Potenza"], ["PO", "Prato"],
  ["RG", "Ragusa"], ["RA", "Ravenna"], ["RC", "Reggio Calabria"], ["RE", "Reggio Emilia"], ["RI", "Rieti"],
  ["RN", "Rimini"], ["RM", "Roma"], ["RO", "Rovigo"], ["SA", "Salerno"], ["SS", "Sassari"],
  ["SV", "Savona"], ["SI", "Siena"], ["SR", "Siracusa"], ["SO", "Sondrio"], ["SU", "Sud Sardegna"],
  ["TA", "Taranto"], ["TE", "Teramo"], ["TR", "Terni"], ["TO", "Torino"], ["TP", "Trapani"],
  ["TN", "Trento"], ["TV", "Treviso"], ["TS", "Trieste"], ["UD", "Udine"], ["VA", "Varese"],
  ["VE", "Venezia"], ["VB", "Verbano-Cusio-Ossola"], ["VC", "Vercelli"], ["VR", "Verona"], ["VV", "Vibo Valentia"],
  ["VI", "Vicenza"], ["VT", "Viterbo"],
].sort((a, b) => a[1].localeCompare(b[1]));

const BASE_DOCS = [
  { key: "titolo_autodichiarazione", label: "Autodichiarazione sostitutiva del titolo di studio", group: "titolo", testid: "file-upload-autodichiarazione" },
  { key: "titolo_diploma", label: "Diploma (in alternativa all'autodichiarazione)", group: "titolo", testid: "file-upload-diploma" },
  { key: "titolo_dichiarazione_valore", label: "Dichiarazione di valore del titolo di studio (extra-UE)", group: "titolo_ue", testid: "file-upload-dichiarazione-valore" },
  { key: "carta_identita", label: "Copia carta d'identità", required: true, testid: "file-upload-carta-identita" },
  { key: "codice_fiscale_doc", label: "Copia codice fiscale", required: true, testid: "file-upload-codice-fiscale" },
  { key: "curriculum", label: "Curriculum vitae", required: true, testid: "file-upload-curriculum" },
  { key: "lettera_motivazionale", label: "Lettera motivazionale (facoltativo)", testid: "file-upload-lettera" },
  { key: "cert_inglese_b1", label: "Certificato di conoscenza della lingua inglese, livello B1 o superiore, non scaduto (facoltativo — esenta dalla parte di inglese del test A)", testid: "file-upload-english-cert" },
  { key: "cert_ecdl", label: "Certificazione informatica ECDL/ICDL (facoltativo — esenta dalla categoria informatica)", testid: "file-upload-it-cert" },
];

export default function ApplyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [tenant, setTenant] = useState(null);
  const [tenantError, setTenantError] = useState(false);
  const [courses, setCourses] = useState([]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [prefs, setPrefs] = useState(["", "", ""]);
  const [anagrafica, setAnagrafica] = useState({ extra_ue: false });
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [missingDocs, setMissingDocs] = useState([]);

  // Il tenant è determinato dal contesto: pagina dedicata dell'istituto visitata in precedenza
  useEffect(() => {
    const slug = localStorage.getItem("its_tenant_slug");
    if (!slug) { setTenantError(true); return; }
    api.get(`/tenants/slug/${slug}`)
      .then(({ data }) => {
        if (!data.active || !data.applications_open) { setTenantError(true); return; }
        setTenant(data);
        if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || window.innerWidth < 768) {
          api.post(`/tenants/slug/${slug}/mobile-notice`).catch(() => {});
        }
        api.get(`/courses?tenant_id=${data.id}`).then(({ data: c }) => setCourses(c)).catch(() => {});
      })
      .catch(() => setTenantError(true));
  }, []);

  const extraUe = Boolean(anagrafica.extra_ue);
  const step0Valid = privacyAccepted;
  const step1Valid = Boolean(prefs[0]);
  const step2Valid = ANAG_FIELDS.every((f) => String(anagrafica[f.key] || "").trim()) &&
    (!extraUe || String(anagrafica.permesso_soggiorno || "").trim());
  const titoloOk = extraUe ? Boolean(files.titolo_dichiarazione_valore)
    : Boolean(files.titolo_autodichiarazione || files.titolo_diploma);
  const step3Valid = titoloOk && BASE_DOCS.filter((d) => d.required).every((d) => files[d.key]);
  const stepValid = [step0Valid, step1Valid, step2Valid, step3Valid][step];

  // Gli input testuali sono non controllati (defaultValue): l'autofill del browser
  // sopravvive ai re-render. I valori vengono raccolti dal DOM al click su Avanti.
  const harvestAnagrafica = () => {
    const harvested = {};
    [...ANAG_FIELDS.map((f) => f.key), "permesso_soggiorno"].forEach((k) => {
      const el = document.getElementById(k);
      if (el && el.value) harvested[k] = el.value;
    });
    const merged = { ...anagrafica, ...harvested };
    if (Object.keys(harvested).length) setAnagrafica(merged);
    const missing = ANAG_FIELDS.filter((f) => !String(merged[f.key] || "").trim()).map((f) => f.key);
    if (merged.extra_ue && !String(merged.permesso_soggiorno || "").trim()) missing.push("permesso_soggiorno");
    return { valid: missing.length === 0, missing };
  };

  const goNext = () => {
    if (step === 2) {
      const { valid, missing } = harvestAnagrafica();
      if (!valid) {
        setInvalidFields(missing);
        toast.error(`Campi mancanti: ${missing.map((k) => FIELD_LABELS[k]).join(", ")}`);
        const first = document.getElementById(missing[0]);
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setInvalidFields([]);
    }
    setStep(step + 1);
  };

  const submit = async () => {
    const missing = [];
    if (!titoloOk) {
      if (extraUe) missing.push("titolo_dichiarazione_valore");
      else missing.push("titolo_autodichiarazione", "titolo_diploma");
    }
    BASE_DOCS.filter((d) => d.required && !files[d.key]).forEach((d) => missing.push(d.key));
    if (missing.length) {
      setMissingDocs(missing);
      toast.error("Mancano alcuni documenti obbligatori");
      const first = document.getElementById(missing[0]);
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setMissingDocs([]);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...anagrafica, privacy_accepted: true }));
      fd.append("course_ids", JSON.stringify(prefs.filter(Boolean)));
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Candidatura inviata con successo");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (tenantError) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="apply-no-tenant">
          <ShieldCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h1 className="font-display text-xl font-bold text-slate-900">Candidatura non disponibile</h1>
          <p className="mt-2 text-sm text-slate-600">
            Per candidarti devi partire dalla pagina dedicata dell'istituto ITS di tuo interesse.
          </p>
          <Button data-testid="apply-go-home" className="mt-6 bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/")}>
            Vai alla home
          </Button>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="apply-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <p className="eyebrow mb-2">Candidatura telematica · {tenant.name}</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Domanda di ammissione</h1>

        <div className="mt-8 flex items-center gap-2" data-testid="form-steps-indicator">
          {STEP_TITLES.map((t, i) => (
            <div key={t} className="flex items-center gap-2 flex-1">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold border ${i < step ? "bg-[#003366] text-white border-[#003366]" : i === step ? "border-[#003366] text-[#003366]" : "border-slate-300 text-slate-400"}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span data-testid={`form-step-${i}`} className={`text-xs sm:text-sm ${i === step ? "text-slate-900 font-medium" : "text-slate-400"} hidden sm:inline`}>{t}</span>
              {i < STEP_TITLES.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </div>
          ))}
        </div>

        <Card className="institutional-card mt-8" data-testid="candidate-onboarding-form">
          <CardContent className="p-6 sm:p-8">
            {step === 0 && (
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-[#E0E7FF] text-[#003366] flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-slate-900">Privacy Policy</h2>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                      Prima di procedere, leggi l'informativa sul trattamento dei dati personali dell'istituto.
                    </p>
                  </div>
                </div>
                <label data-testid="privacy-accept-label" className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4 cursor-pointer">
                  <Checkbox data-testid="privacy-accept-checkbox" checked={privacyAccepted}
                    onCheckedChange={(v) => setPrivacyAccepted(Boolean(v))} className="mt-0.5" />
                  <span className="text-sm text-slate-700 leading-relaxed">
                    Ho letto e accetto la{" "}
                    <Link data-testid="privacy-link" to={`/candidati/${tenant.slug}/privacy`} target="_blank"
                      className="text-[#003366] font-medium underline">
                      Privacy Policy dell'istituto
                    </Link>
                  </span>
                </label>
                {!privacyAccepted && (
                  <p className="text-xs text-slate-500">Non puoi procedere senza aver accettato la Privacy Policy.</p>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Indica le tue preferenze tra i corsi di <strong>{tenant.name}</strong>: la prima preferenza è
                  obbligatoria, seconda e terza sono facoltative.
                </p>
                {["Prima preferenza", "Seconda preferenza (facoltativa)", "Terza preferenza (facoltativa)"].map((label, i) => {
                  const others = prefs.filter((p, j) => p && j !== i);
                  return (
                    <div key={label}>
                      <Label>{label}{i === 0 ? " *" : ""}</Label>
                      <Select value={prefs[i]} onValueChange={(v) => { const next = [...prefs]; next[i] = v; setPrefs(next); }}>
                        <SelectTrigger data-testid={`course-pref-${i + 1}`} className="mt-2">
                          <SelectValue placeholder="Seleziona il corso" />
                        </SelectTrigger>
                        <SelectContent>
                          {courses.filter((c) => !others.includes(c.id)).map((c) => (
                            <SelectItem data-testid={`course-pref-${i + 1}-option-${c.id}`} key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {ANAG_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label htmlFor={f.key}>{f.label} *</Label>
                      {f.select ? (
                        <Select value={anagrafica[f.key] || ""}
                          onValueChange={(v) => { setAnagrafica({ ...anagrafica, [f.key]: v }); setInvalidFields((p) => p.filter((k) => k !== f.key)); }}>
                          <SelectTrigger data-testid={f.testid} id={f.key}
                            aria-invalid={invalidFields.includes(f.key)}
                            className={invalidFields.includes(f.key) ? "border-red-500 bg-red-50" : ""}>
                            <SelectValue placeholder="Seleziona la provincia" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {PROVINCE.map(([sigla, nome]) => (
                              <SelectItem data-testid={`form-field-provincia-option-${sigla}`} key={sigla} value={sigla}>{sigla} - {nome}</SelectItem>
                            ))}
                            <SelectItem data-testid="form-field-provincia-option-EE" value="EE">EE - Estero</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input data-testid={f.testid} id={f.key} type={f.type || "text"} defaultValue={anagrafica[f.key] || ""}
                          aria-invalid={invalidFields.includes(f.key)}
                          className={invalidFields.includes(f.key) ? "border-red-500 bg-red-50 focus-visible:ring-red-500" : ""}
                          onChange={(e) => { setAnagrafica({ ...anagrafica, [f.key]: e.target.value }); setInvalidFields((p) => p.filter((k) => k !== f.key)); }} />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Il codice fiscale è univoco per istituto: non è possibile inviare due candidature con lo stesso codice fiscale allo stesso istituto.</p>
                <label className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4 cursor-pointer">
                  <Checkbox data-testid="form-field-extra-ue" checked={extraUe}
                    onCheckedChange={(v) => setAnagrafica({ ...anagrafica, extra_ue: Boolean(v) })} className="mt-0.5" />
                  <span className="text-sm text-slate-700">Sono un cittadino/a extra-UE (richiede permesso di soggiorno e dichiarazione di valore del titolo di studio)</span>
                </label>
                {extraUe && (
                  <div>
                    <Label htmlFor="permesso_soggiorno">Numero / riferimento permesso di soggiorno *</Label>
                    <Input data-testid="form-field-permesso-soggiorno" id="permesso_soggiorno" defaultValue={anagrafica.permesso_soggiorno || ""}
                      aria-invalid={invalidFields.includes("permesso_soggiorno")}
                      className={invalidFields.includes("permesso_soggiorno") ? "border-red-500 bg-red-50 focus-visible:ring-red-500" : ""}
                      onChange={(e) => { setAnagrafica({ ...anagrafica, permesso_soggiorno: e.target.value }); setInvalidFields((p) => p.filter((k) => k !== "permesso_soggiorno")); }} />
                  </div>
                )}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid="form-field-dsa" checked={Boolean(anagrafica.dsa_declared)}
                      onCheckedChange={(v) => setAnagrafica({ ...anagrafica, dsa_declared: Boolean(v) })} className="mt-0.5" />
                    <span className="text-sm text-slate-700">Dichiaro di avere un certificato DSA in corso di validità</span>
                  </label>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="form-dsa-note">
                    Se la diagnosi è antecedente ai 18 anni, deve essere stata aggiornata da non più di 3 anni.
                    Se è stata effettuata dopo i 18 anni, è sempre valida (Legge 170/2010). Il certificato non viene
                    caricato né conservato sulla piattaforma: potrà essere chiesto in sede di colloquio.
                  </p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <p className="text-sm text-slate-600">Formati ammessi: PDF, JPG, PNG (max 15MB per file).</p>
                {missingDocs.length > 0 && !step3Valid && (
                  <div data-testid="missing-docs-error" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm font-semibold text-red-700">
                    Mancano alcuni documenti obbligatori: quelli da caricare sono evidenziati in rosso.
                  </div>
                )}
                {!extraUe && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                    Titolo di studio: carica l'autodichiarazione sostitutiva <strong>oppure</strong> il diploma (almeno uno dei due, obbligatorio).
                  </div>
                )}
                {extraUe && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                    Per cittadini extra-UE la <strong>dichiarazione di valore</strong> del titolo di studio è obbligatoria.
                  </div>
                )}
                {BASE_DOCS.map((d) => {
                  const showMissing = missingDocs.includes(d.key) && !files[d.key] && !(d.group === "titolo" && titoloOk);
                  return (
                  <div key={d.key} className={`relative border border-dashed rounded-lg p-4 ${showMissing ? "border-red-400 bg-red-50" : "border-slate-300"}`}>
                    {files[d.key] && (
                      <button
                        type="button"
                        data-testid={`file-remove-${d.key}`}
                        aria-label={`Rimuovi ${d.label}`}
                        title="Rimuovi il file e sostituiscilo"
                        onClick={() => setFiles((prev) => { const next = { ...prev }; delete next[d.key]; return next; })}
                        className="absolute top-3 right-3 h-6 w-6 rounded-full bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-600 flex items-center justify-center transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <Label htmlFor={d.key} className="flex flex-wrap items-center gap-2 pr-8">
                      <FileUp className="h-4 w-4 text-[#0284C7]" /> {d.label}
                      {d.required && <span className="text-xs font-semibold text-red-600">* Obbligatorio</span>}
                      {d.group === "titolo_ue" && extraUe && <span className="text-xs font-semibold text-red-600">* Obbligatorio</span>}
                    </Label>
                    <Input key={`${d.key}-${files[d.key]?.name || "empty"}`} data-testid={d.testid} id={d.key} type="file" accept=".pdf,.jpg,.jpeg,.png" className="mt-2"
                      onChange={(e) => { setFiles({ ...files, [d.key]: e.target.files?.[0] || null }); setMissingDocs((prev) => prev.filter((k) => k !== d.key)); }} />
                    {showMissing && (
                      <p data-testid={`doc-missing-${d.key}`} className="mt-1 text-xs font-semibold text-red-600">Obbligatorio, da caricare</p>
                    )}
                    {files[d.key] && <p className="mt-1 text-xs text-green-700">Selezionato: {files[d.key].name} — usa la X in alto a destra per rimuoverlo e sostituirlo</p>}
                  </div>
                  );
                })}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
                  Inviando la candidatura dichiari che i dati e i documenti forniti sono veritieri. La selezione
                  prosegue con un test online proctored (webcam attiva e schermo intero obbligatorio).
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-between">
              <Button data-testid="form-prev-button" variant="outline" disabled={step === 0 || busy} onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Indietro
              </Button>
              {step < 3 ? (
                <Button data-testid="form-next-button" className="bg-[#003366] hover:bg-[#002244]" disabled={step !== 2 && !stepValid} onClick={goNext}>
                  Avanti <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button data-testid="form-submit-button" className="bg-[#E05A47] hover:bg-[#c94836]" disabled={busy} onClick={submit}>
                  {busy ? "Invio in corso..." : "Invia candidatura"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {busy && (
        <div data-testid="application-submitting-overlay" className="fixed inset-0 z-50 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366]" />
          <p className="font-display text-lg font-semibold text-slate-900">Stiamo registrando la tua candidatura</p>
          <p className="text-sm text-slate-500 max-w-sm">Attendi qualche secondo, non chiudere la pagina: stiamo caricando i tuoi documenti in sicurezza.</p>
        </div>
      )}
    </div>
  );
}
